import { useTranslation } from 'react-i18next';
import { RoleControllerService } from '../../api/services/RoleControllerService';
import type { RoleDto } from '../../api/models/RoleDto';
import { CrudPage } from '../../components/CrudPage';
import { useErrorHandler } from '../../utils/errorHandler';
import UserRoleFormDialog from '../forms/UserRoleFormDialog';

const UserRolesPage = () => {
  const { handleError } = useErrorHandler();
  const { t } = useTranslation(['common']);

  // Convert CancelablePromise to Promise for CrudPage compatibility
  const fetchData = async (): Promise<(RoleDto & { id: string })[]> => {
    try {
      const roles = await RoleControllerService.findAll();
      // Filter out roles with undefined id and ensure id is string
      return roles
        .filter((role): role is RoleDto & { id: string } => typeof role.uuid === 'string')
        .map(role => ({ ...role, id: role.uuid || '' }));
    } catch (error) {
      handleError(error, 'fetching roles');
      return [];
    }
  };

  const saveData = async (data: RoleDto & { id: string }): Promise<RoleDto & { id: string }> => {
    try {
      if (data.id && data.id.length > 0) {
        // Update existing role
        await RoleControllerService.update(data.id, data);
        return data; // Return the updated data
      } else {
        // Create new role - the backend should return the created role with ID
        const createdRole = await RoleControllerService.create(data);
        // Ensure the returned role has an id field
        return {
          ...createdRole,
          id: createdRole.uuid || ''
        };
      }
    } catch (error) {
      handleError(error, 'saving role');
      throw error;
    }
  };

  const deleteData = async (id: string): Promise<void> => {
    try {
      await RoleControllerService.delete(id);
    } catch (error) {
      handleError(error, 'deleting role');
      throw error;
    }
  };

  return (
    <CrudPage<RoleDto & { id: string }>
      titleKey="headers.userRoles"
      gridMeta={[
        { field: 'name', headerKey: 'userRole.grid.field.name', width: 150 },
        { field: 'description', headerKey: 'userRole.grid.field.description', width: 200 },
        { field: 'tenantName', headerKey: 'userRole.grid.field.tenant', width: 150 },
        {
          field: 'isSystem',
          headerKey: 'userRole.grid.field.type',
          width: 110,
          render: (row: any) => row.isSystem
            ? <span className="inline-block px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">{t('userRole.type.system')}</span>
            : <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">{t('userRole.type.normal')}</span>
        }
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={UserRoleFormDialog}
      entityName="role"
    />
  );
};

export default UserRolesPage;
